import { redirect } from '@remix-run/node';
import Debug from 'debug';
import { getSortParamValue } from '~/components/shared/SortBy';
import type {
  Category,
  ConfigurationType,
  ItemAttributes,
  PackBuilderPricesResponse,
  PackItem,
  PackItemPrice,
  PackProduct,
  Product,
  ProductsAvailableFilters,
  ProductsPricesRequest,
  SupplierProductsPricesRequest,
} from '~/services/api.server';
import { scApi } from '~/services/api.server';
import { getAttributes, isPackProduct } from '~/utils/common';
import {
  AUTOMOCKUPS_PARAM,
  CATEGORIES_PARAM,
  DESIGN_STATUS_PARAM,
  DOMAIN_PARAM,
  LOGO_PARAM,
  MAX_AMOUNT_PARAM,
  MIN_AMOUNT_PARAM,
  POD_PARAM,
  PRODUCTION_TIME_PARAM,
  SEARCH_PARAM,
  TYPES_PARAM,
  getPageParamValue,
  getSearchParamValue,
} from '~/utils/searchParams';

export interface CategoryNode extends Category {
  href: string;
}

const debug = Debug('sc:mango:route:shop');

export function RemoveUnsetSearchParams(url: URL) {
  const toDelete: string[] = [];

  url.searchParams.forEach((value, param) => {
    if (value === '') {
      toDelete.push(param);
    }
  });

  if (toDelete.length > 0) {
    const searchParams = new URLSearchParams(url.searchParams);

    toDelete.forEach((param) => {
      searchParams.delete(param);
    });
    const sp = searchParams.toString();
    throw redirect(url.pathname + (sp ? '?' + sp : ''));
  }
}

function getTypesParamValuesSet(searchParams: URLSearchParams) {
  return new Set(
    searchParams
      .getAll(TYPES_PARAM)
      .map((v) => parseInt(v, 10))
      .filter((v) => v > 0)
  );
}

function getCategoriesParamValuesSet(searchParams: URLSearchParams) {
  return new Set(searchParams.getAll(CATEGORIES_PARAM).filter(Boolean));
}

export function getMinPriceParamValue(searchParams: URLSearchParams) {
  const value = searchParams.get(MIN_AMOUNT_PARAM);
  return value ? parseInt(value, 10) : -1;
}

export function getMaxPriceParamValue(searchParams: URLSearchParams) {
  const value = searchParams.get(MAX_AMOUNT_PARAM);
  return value ? parseInt(value, 10) : Number.MAX_VALUE;
}

export function getProductionTimeParamValue(searchParams: URLSearchParams) {
  const value = searchParams.get(PRODUCTION_TIME_PARAM);
  return value ? parseInt(value, 10) : Number.MAX_VALUE;
}

export function getIsPodParamValue(searchParams: URLSearchParams) {
  const value = searchParams.get(POD_PARAM);
  return value === 'true' ? true : false;
}

export const productsSortOptions = {
  '-num_views': 'Popular',
  '-num_orders': 'Best sellers',
  price: 'Price, low to high',
  '-price': 'Price, high to low',
  production_time: 'Production time, low to high',
  '-production_time': 'Production time, high to low',
} as const;

export const defaultSortValue: keyof typeof productsSortOptions = '-num_views';

export const myProductsSortOptions = {
  '-created_at': 'Newest first',
  created_at: 'Oldest first',
  price: 'Price, low to high',
  '-price': 'Price, high to low',
  production_time: 'Production time, low to high',
  '-production_time': 'Production time, high to low',
} as const;

const defaultMyProductsSortOption: keyof typeof myProductsSortOptions = '-created_at';

function getDesignStatusParamValue(searchParams: URLSearchParams) {
  return new Set(
    searchParams
      .getAll(DESIGN_STATUS_PARAM)
      .map((v) => parseInt(v, 10))
      .filter((v) => v > 0)
  );
}

export const productsPerPage = 24;

export function getProducts(
  searchParams: URLSearchParams,
  accessToken: string | undefined,
  accountId: number | undefined,
  filters: ProductsAvailableFilters,
  additionalQuery?: { [key: string]: string },
  isDashboard?: boolean
) {
  const categories = getCategoriesParamValuesSet(searchParams);
  const types = getTypesParamValuesSet(searchParams);

  const minPriceAmount = Math.max(filters.price?.min, getMinPriceParamValue(searchParams));
  const maxPriceAmount = Math.min(filters.price?.max, getMaxPriceParamValue(searchParams));

  const time = Math.min(filters.production_time.max, getProductionTimeParamValue(searchParams));

  const page = getPageParamValue(searchParams);
  const offset = ((page - 1) * productsPerPage).toString();
  const ordering = getSortParamValue(
    isDashboard ? myProductsSortOptions : productsSortOptions,
    isDashboard ? defaultMyProductsSortOption : defaultSortValue,
    searchParams
  );

  const search = getSearchParamValue(searchParams);

  const isPod = getIsPodParamValue(searchParams);

  const designStatus = getDesignStatusParamValue(searchParams);

  const query = {
    categories__in: [...categories].join(),
    type__in: [...types].join(),
    price__gte: minPriceAmount.toString(),
    price__lte: maxPriceAmount.toString(),
    production_time__lte: time.toString(),
    limit: productsPerPage.toString(),
    offset,
    ordering,
    ...(isPod ? { [POD_PARAM]: isPod.toString() } : {}),
    ...(designStatus ? { [DESIGN_STATUS_PARAM]: [...designStatus].join() } : {}),
    expand: 'selected_mockup',
    ...(search ? { [SEARCH_PARAM]: search } : {}),
    ...(additionalQuery ? additionalQuery : {}),
  };

  debug('getProducts query:', query);

  return scApi.getProducts(accessToken, accountId, query);
}

export const getBulkProductsWithLogo = (
  showAutomockupsFlag: boolean,
  searchParams: URLSearchParams,
  accessToken: string | undefined,
  accountId: number,
  productsFilters: ProductsAvailableFilters,
  isDashboard?: boolean
) => {
  const logoParam = searchParams.get(LOGO_PARAM);
  const automockupsActiveParam = searchParams.get(AUTOMOCKUPS_PARAM) === 'true';
  const domainParam = searchParams.get(DOMAIN_PARAM);

  let additionalQuery = undefined;
  if (showAutomockupsFlag && automockupsActiveParam) {
    if (logoParam) {
      additionalQuery = { display_logo: logoParam };
    } else if (domainParam) {
      additionalQuery = { domain: domainParam };
    }
  }
  return getProducts(searchParams, accessToken, accountId, productsFilters, additionalQuery, isDashboard);
};

export function getCategoryNode(category: Category, basePath: string) {
  return {
    ...category,
    href: `${basePath}/${category.slug}`,
  };
}

export function getSubCategoriesNodes(subcategories: Category[], basePath: string) {
  return subcategories.map((category) => {
    let subcategory = { ...category } as CategoryNode;

    subcategory = getCategoryNode(subcategory, basePath);

    if (subcategory.subcategories.length > 0) {
      subcategory.subcategories = getSubCategoriesNodes(subcategory.subcategories, subcategory.href);
    }

    return subcategory;
  });
}

function buildCategoriesTree(
  categories: Category[],
  path: string[],
  categoryNodes: Array<CategoryNode | CategoryNode[]>,
  basePath: string
): string {
  if (categories.length === 0) return basePath;

  const rest = path.slice();
  const current = rest.shift();

  const category = categories.find((c) => c.slug === current);

  if (!category) {
    categoryNodes.push(categories.map((c) => getCategoryNode(c, basePath)));
    return basePath;
  }

  categoryNodes.push(getCategoryNode(category, basePath));

  return buildCategoriesTree(category.subcategories, rest, categoryNodes, `${basePath}/${category.slug}`);
}

function getSelectedNode(nodes: Array<CategoryNode | CategoryNode[]>, path: string): CategoryNode | undefined {
  let selectedNode: CategoryNode | undefined = undefined;

  for (const node of nodes) {
    if (Array.isArray(node)) {
      const selected = getSelectedNode(node, path);
      if (selected) {
        selectedNode = selected;
      }
    } else if (node.href === path) {
      selectedNode = node;
    }
  }

  return selectedNode;
}

export function getCategoriesList(
  categories: Category[],
  path: string[],
  basePath: string
): [CategoryNode | undefined, string, Array<CategoryNode | CategoryNode[]>] {
  const categoryNodes: Array<CategoryNode | CategoryNode[]> = [];

  const validatedPath = buildCategoriesTree(categories, path, categoryNodes, basePath);
  const selectedNode = getSelectedNode(categoryNodes, validatedPath);

  return [selectedNode, validatedPath, categoryNodes];
}

export function getCategories(query?: URLSearchParams): Promise<Category[]> {
  return new Promise(async (resolve) => {
    const filteredCategories = await scApi.getCategories(query).then((r) => r.results);
    if (filteredCategories.length > 0) {
      resolve(filteredCategories);
    }

    const allCategories = await scApi.getCategories().then((r) => r.results);
    resolve(allCategories);
  });
}

export function getBulkSwagCategories(): Promise<Category[]> {
  return new Promise(async (resolve) => {
    const filteredCategories = await scApi.getBulkSwagCategories().then((r) => r.results);
    if (filteredCategories.length > 0) {
      resolve(filteredCategories);
    }

    const allCategories = await scApi.getBulkSwagCategories().then((r) => r.results);
    resolve(allCategories);
  });
}

export function getSwagPackCategories(): Promise<Category[]> {
  return new Promise(async (resolve) => {
    const filteredCategories = await scApi.getSwagPacksCategories().then((r) => r.results);
    if (filteredCategories.length > 0) {
      resolve(filteredCategories);
    }

    const allCategories = await scApi.getBulkSwagCategories().then((r) => r.results);
    resolve(allCategories);
  });
}

export function getPricesRequest({
  product,
  quantity,
  packAttributes,
  configuration_type,
}: {
  product: Product;
  quantity: number;
  packAttributes: ItemAttributes[];
  configuration_type: ConfigurationType;
}) {
  const pricesRequest: ProductsPricesRequest | SupplierProductsPricesRequest = isPackProduct(product)
    ? {
        products: [
          {
            product: product.id,
            quantity,
            items: packAttributes.map((attribute: ItemAttributes) => {
              return {
                product: attribute.product_id,
                attributes: getAttributes(attribute.attributes || []),
                configuration_type,
              };
            }),
          },
        ],
      }
    : {
        products: [
          {
            product: product.id,
            quantity,
            attributes: getAttributes(packAttributes[0].attributes || []),
            configuration_type,
          },
        ],
      };

  return pricesRequest;
}

export function getItemPriceObject(productId: number | undefined, prices: PackItemPrice[]) {
  if (!productId || !prices) return undefined;
  const price = prices.find((price: PackItemPrice) => {
    return price.product === productId;
  });
  return price?.price;
}

export function replacePackBuilderPrices(product: PackProduct, prices: PackBuilderPricesResponse) {
  product.prices.price = prices.pack_price;
  product.items?.forEach((item: PackItem) => {
    const { product } = item;
    if (product) {
      product.prices.price = prices.products.find((p) => p.product === product.id)?.price || product.prices.price;
    }
  });
  return product;
}
